import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import { Analytics } from '@vercel/analytics/react';
import Login from './components/Login';
import ClientLogin from './components/ClientLogin';
import LoginHome from './components/LoginHome';
import InitialSetup from './components/InitialSetup';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import MainLayout from './components/MainLayout';
import ResetPassword from './components/ResetPassword';
import UpdateEmail from './components/UpdateEmail';
import ProfilePage from './pages/ProfilePage';
import ClientDetailsRoute from './components/ClientDetailsRoute';
import ClientSummaryRoute from './components/ClientSummaryRoute';
import ClientDiscussionRoute from './components/ClientDiscussionRoute';
import ClientNotesRoute from './components/ClientNotesRoute';
import ClientContractRoute from './components/ClientContractRoute';
import ClientReportRoute from './components/ClientReportRoute';
import ClientPlanningRoute from './components/ClientPlanningRoute';
import OverviewPage from './components/OverviewPage';
import ClientDashboard from './components/ClientDashboard';
import ClientRoute from './components/ClientRoute';
import AdminRoute from './components/AdminRoute';
import UserRedirect from './components/UserRedirect';
import AutoRoleSetupPage from './pages/AutoRoleSetupPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionManagementPage from './pages/SubscriptionManagementPage';
import StripeConfigPage from './pages/StripeConfigPage';
import StripeProductsPage from './pages/StripeProductsPage';
import SubscriptionGuard from './components/SubscriptionGuard';
import InitializationService from './services/initializationService';
import BeneficiaryMandatePanel from './components/BeneficiaryMandatePanel';
import NFCRedirect from './components/NFCRedirect';
import { ToastProvider } from './contexts/ToastContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import MobileLoading from './components/MobileLoading';
import { setupErrorHandling } from './utils/errorHandler';
import { getSubscriptionConfig } from './config/subscriptionConfig';
import NFCListenerWrapper from './components/NFCListenerWrapper';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsInitialization, setNeedsInitialization] = useState(false);
  const [checkingInitialization, setCheckingInitialization] = useState(true);

  // Initialiser la gestion d'erreurs globale
  useEffect(() => {
    setupErrorHandling();
  }, []);

  // Charger la configuration du module d'abonnement au démarrage
  useEffect(() => {
    const loadSubscriptionConfig = async () => {
      try {
        await getSubscriptionConfig();
        console.log('✅ Configuration du module d\'abonnement chargée');
      } catch (error) {
        console.error('❌ Erreur lors du chargement de la configuration d\'abonnement:', error);
      }
    };

    loadSubscriptionConfig();
  }, []);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        console.warn('Chargement prolongé, forcer l\'arrêt');
        setLoading(false);
      }
    }, 10000); // 10 secondes

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      clearTimeout(loadingTimeout);
      setUser(user);
      setLoading(false);
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, []);

  // Vérifier si l'application nécessite une initialisation
  useEffect(() => {
    const checkInitialization = async () => {
      try {
        const status = await InitializationService.getInitializationStatus();
        console.log('🔍 Statut d\'initialisation:', status);
        setNeedsInitialization(status.needsInitialization);
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'initialisation:', error);
        // En cas d'erreur, supposer que l'initialisation n'est pas nécessaire
        setNeedsInitialization(false);
      } finally {
        setCheckingInitialization(false);
      }
    };

    // Vérifier l'initialisation dans tous les cas
    if (!loading) {
      checkInitialization();
    }
  }, [loading]);

  if (loading || checkingInitialization) {
    return (
      <ThemeProvider>
        <MobileLoading />
      </ThemeProvider>
    );
  }

  // Empêcher MainLayout de se rendre pendant l'initialisation
  // Si l'utilisateur est connecté mais l'application nécessite une initialisation,
  // afficher l'écran d'initialisation au lieu de MainLayout
  if (user && needsInitialization) {
    return (
      <ThemeProvider>
        <ToastProvider>
          <Router>
            <NFCListenerWrapper user={user}>
              <Routes>
                <Route path="/*" element={<InitialSetup />} />
              </Routes>
            </NFCListenerWrapper>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <Router>
          <NFCListenerWrapper user={user}>
            <Routes>
              <Route path="/" element={user ? <UserRedirect user={user} /> : (needsInitialization ? <InitialSetup /> : <LoginHome />)} />
              <Route path="/initial-setup" element={needsInitialization ? <InitialSetup /> : <Navigate to="/" />} />
              <Route path="/login" element={user ? <UserRedirect user={user} /> : <Login />} />
              <Route path="/register" element={user ? <UserRedirect user={user} /> : <Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/update-email" element={<UpdateEmail />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />

              {/* Route de redirection NFC - publique */}
              <Route path="/nfc" element={<NFCRedirect />} />

              {/* Routes d'administration - protégées par AdminRoute et SubscriptionGuard */}
              <Route path="/client-details/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientDetailsRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/client-summary/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientSummaryRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/client-discussion/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientDiscussionRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/client-notes/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientNotesRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/client-contract/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientContractRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/client-report/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientReportRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/client-planning/:clientId" element={<SubscriptionGuard><AdminRoute user={user}><ClientPlanningRoute db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/overview" element={<SubscriptionGuard><AdminRoute user={user}><OverviewPage db={db} currentUser={user} /></AdminRoute></SubscriptionGuard>} />
              <Route path="/auto-role-setup" element={<SubscriptionGuard><AdminRoute user={user}><AutoRoleSetupPage /></AdminRoute></SubscriptionGuard>} />
              <Route path="/stripe-config" element={<SubscriptionGuard><AdminRoute user={user}><StripeConfigPage /></AdminRoute></SubscriptionGuard>} />
              <Route path="/stripe-products" element={<SubscriptionGuard><AdminRoute user={user}><StripeProductsPage /></AdminRoute></SubscriptionGuard>} />
              <Route path="/subscription-management" element={<SubscriptionGuard><SubscriptionManagementPage /></SubscriptionGuard>} />
              <Route path="/beneficiary-mandates" element={<SubscriptionGuard><AdminRoute user={user}><BeneficiaryMandatePanel /></AdminRoute></SubscriptionGuard>} />

              {/* Routes client - protégées par ClientRoute */}
              <Route path="/client-login" element={user ? <UserRedirect user={user} /> : <ClientLogin />} />
              <Route path="/client-dashboard/:clientId" element={
                <ClientRoute user={user}><ClientDashboard db={db} /></ClientRoute>
              } />

              {/* Route principale pour les employés avec sous-routes */}
              <Route path="/dashboard/:view?" element={user ? (
                <MessagingProvider db={db} currentUser={user}>
                  <MainLayout />
                </MessagingProvider>
              ) : <Navigate to="/" />} />

              <Route
                path="/*"
                element={user ? (
                  <MessagingProvider db={db} currentUser={user}>
                    <MainLayout />
                  </MessagingProvider>
                ) : <Navigate to="/" />}
              />
            </Routes>
            <Analytics />
          </NFCListenerWrapper>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
